import _ from "lodash";
/**--external */
import React, { useRef } from "react";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import classNames from "classnames";

/**--internal-- */
import withLoader from "UIComponents/GqlWithLoader";
import { getApplicableFiltersQuery } from "../../modules/Queries";
import { getApplicableFiltersFromCache } from "../../modules/GraphqlHelpers";
import { tabFiltersConfig } from "../../modules/Config";
import { getUpdatedModuleFacets, getFilterLabel } from "../../modules/Utils";
import { getApplicableFiltersInfoMemoized } from "../DynamicFiltersWrapper/DynamicFilters/utils";
import SearchInput from "../DynamicFiltersWrapper/DynamicFilters/SearchInput";
import { ELEMENT_TYPE_MYP_OBJECTIVES } from "Constants/stringConstants";
import FilterGroup from "../DynamicFiltersWrapper/DynamicFilters/FilterGroup";
import { useI18n } from "Hooks";
import { Button } from "@toddle-edu/ds-web";

/**--relative-- */
import classes from "./DynamicFiltersWrapper.scss";
import DynamicFilter from "./DynamicFilters";
import MoreFiltersButton from "./MoreFiltersButton";
import {
  moreFilterGrpContainerStyle,
  moreFiltersStyle,
  containerStyle,
  filterGrpContainerStyle,
  buttonDropDownContainerStyle,
} from "./DynamicFiltersWrapperStyles";
import { getParentFiltersObj } from "./utils";

const DynamicFiltersWrapper = props => {
  const {
    isSearchApplicable,
    areFiltersApplicable,
    searchText,
    applicableFilters,
    placeholder,
    updateSearchText,
    areMoreFiltersVisible,
    updateMoreFiltersVisibilityStatus,
    appliedFilters,
    curriculumProgramId,
    updateAppliedFilters,
    activeTab,
    updateFilterOptions,
    updateParentFilter,
    markDependentFilterAsUpdated,
    parentFilterObj,
    dependentFiltersConfig,
    parentFilters,
    updateLoadingFilters,
    plannerElementSets,
    curriculumType,
    isProjectNode,
    isCompactUINeeded,
    projectNodeDetails,
    postCount,
  } = props;

  const { t } = useI18n(["common", "snp", "progressReport"]);

  const containerParentRef = useRef();

  const headerClasses = classNames({
    [classes.headerWithMarginBottom]: areMoreFiltersVisible,
    [classes.header]: areFiltersApplicable,
    [classes.headerWithOnlySearch]: isSearchApplicable && !areFiltersApplicable,
    [classes.updatedHeader]: isCompactUINeeded,
  });

  const entityTypeInfo = _.get(
    tabFiltersConfig,
    `${activeTab}.entityTypeInfo`,
    {}
  );

  const topRowFilters = _.slice(applicableFilters, 0, 2);

  const moreFilters = _.slice(applicableFilters, 2);

  const parentFiltersData = getParentFiltersObj({
    parentFilters,
    entityTypeInfo,
    plannerElementSets,
    t,
    curriculumType,
    appliedFilters,
  });

  const updateInputField = ({ values, key, isParent = false }) => {
    if (isParent) {
      updateParentFilter({ parent: key });
    }

    updateAppliedFilters({
      appliedFilters: { [key]: values },
    });
  };

  const renderFilter = ({ Component, config, extraProps = {} }) => {
    const filter = config.filter;

    const filterData = _.get(entityTypeInfo, filter, {});

    const dependentFilterParents = _.get(
      _.find(
        dependentFiltersConfig,
        ({ filter: dependentFilter }) => dependentFilter === filter
      ),
      "parents",
      []
    );

    const isParent = _.includes(parentFilters, filter);

    const {
      filterLocale,
      isLabelPresentInSetLabels,
      labelPresentInSetLabelsPerm,
    } = filterData;

    const label = getFilterLabel({
      plannerElementSets,
      t,
      filterLocale,
      labelPresentInSetLabelsPerm,
      isLabelPresentInSetLabels,
      entityType: filter,
      curriculumType,
    });

    const filterClassList = classNames(classes.filter, {
      [classes.mypObjectivesFilter]:
        // Parakh: can have different prop name
        filter == ELEMENT_TYPE_MYP_OBJECTIVES && !isProjectNode,
    });

    return (
      <div className={filterClassList}>
        <Component
          entityType={filter}
          parentFiltersData={parentFiltersData}
          values={appliedFilters[filter]}
          dependentFilterParents={dependentFilterParents}
          markDependentFilterAsUpdated={markDependentFilterAsUpdated}
          parentFilterObj={parentFilterObj}
          {...config}
          label={label}
          isParent={isParent}
          containerParentRef={containerParentRef}
          curriculumProgramId={curriculumProgramId}
          updateInputField={updateInputField}
          updateFilterOptions={updateFilterOptions}
          updateLoadingFilters={updateLoadingFilters}
          baseSetType={filterData.baseSetType}
          curriculumType={curriculumType}
          isProjectNode={isProjectNode}
          isCompactUINeeded={isCompactUINeeded}
          projectNodeDetails={projectNodeDetails}
          {...extraProps}
        />
      </div>
    );
  };

  const resetFilters = () => {
    const activeTabFilterKeys = _.keys(appliedFilters);
    let updatedFilters = {};
    _.forEach(activeTabFilterKeys, key => {
      updatedFilters = {
        ...updatedFilters,
        [key]:
          key == "ACADEMIC_YEAR" ? _.get(appliedFilters, "ACADEMIC_YEAR") : [],
      };
    });
    updateAppliedFilters({ appliedFilters: updatedFilters });
  };

  const getActiveMoreFiltersCount = () => {
    return _.sumBy(moreFilters, ({ filter }) => {
      return +(_.size(appliedFilters[filter]) > 0);
    });
  };

  return (
    <React.Fragment>
      <div className={headerClasses} ref={containerParentRef}>
        {isCompactUINeeded ? (
          <div className={classes.leftContainer}>
            {isSearchApplicable && (
              <SearchInput
                searchText={searchText}
                placeholder={t(placeholder)}
                updateSearchText={updateSearchText}
                key={activeTab}
                isCompactUINeeded={isCompactUINeeded}
              />
            )}
            <div className={"text-label"}>
              {t("progressReport:evidence_count", {
                count: postCount,
              })}
            </div>
          </div>
        ) : (
          isSearchApplicable && (
            <SearchInput
              searchText={searchText}
              placeholder={t(placeholder)}
              updateSearchText={updateSearchText}
              key={activeTab}
            />
          )
        )}
        {areFiltersApplicable && (
          <React.Fragment>
            <FilterGroup
              filters={topRowFilters}
              showRemoveButton={false}
              containerStyle={filterGrpContainerStyle}
            >
              {({ config }) =>
                renderFilter({ Component: DynamicFilter, config })
              }
            </FilterGroup>

            {_.size(applicableFilters) > 2 && (
              <MoreFiltersButton
                onButtonClick={updateMoreFiltersVisibilityStatus}
                isActivated={areMoreFiltersVisible}
                activeMoreFiltersCount={getActiveMoreFiltersCount()}
              />
            )}
            {!isCompactUINeeded ? (
              <div className={classes.resetButtonContainer}>
                <Button
                  variant={"inline-destructive"}
                  size={"large"}
                  onClick={resetFilters}
                >
                  {t("common:reset")}
                </Button>
              </div>
            ) : null}
          </React.Fragment>
        )}
      </div>
      {areFiltersApplicable && _.size(moreFilters) > 0 && (
        <div
          className={classes.moreFilters}
          style={areMoreFiltersVisible ? {} : moreFiltersStyle}
        >
          <FilterGroup
            filters={moreFilters}
            containerStyle={moreFilterGrpContainerStyle}
          >
            {({ config }) =>
              renderFilter({
                Component: DynamicFilter,
                config,
                extraProps: {
                  buttonDropDownContainerStyle,
                  isFilterVisible: areMoreFiltersVisible,
                },
              })
            }
          </FilterGroup>
        </div>
      )}
    </React.Fragment>
  );
};

const mapStateToProps = (state, ownProps) => {
  const {
    activeTab,
    curriculumType,
    isProjectNode,
    projectNodeDetails,
  } = ownProps;

  let applicableFilters = [];

  // Parakh: can have different prop name
  if (isProjectNode) {
    // For projects, we will be using hardcoded data (similar to all, just not coming from BE)
    const applicableFiltersData = _.get(
      projectNodeDetails,
      "applicableFiltersData",
      {}
    );
    const moduleFacets = getUpdatedModuleFacets({
      data: applicableFiltersData,
      activeTab,
      curriculumType,
      isProjectNode,
      projectNodeUID: _.get(projectNodeDetails, "projectNodeUID", ""),
    });
    applicableFilters = getApplicableFiltersInfoMemoized({
      moduleFacets,
      activeTab,
    });
  }

  return {
    isData: true,
    isLoading: false,
    showLoadingIcon: false,
    containerStyle,
    module: _.get(tabFiltersConfig, `${activeTab}.enum`, ""),
    placeholder: _.get(tabFiltersConfig, `${activeTab}.placeholder`, ""),
    parentFilters: _.get(
      tabFiltersConfig,
      `${activeTab}.curriculumWiseParentFilters.${curriculumType}`,
      []
    ),
    dependentFiltersConfig: _.get(
      tabFiltersConfig,
      `${activeTab}.dependentFilterConfig`,
      {}
    ),
    isProjectNode,
    applicableFilters,
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getApplicableFiltersQuery, {
    name: "getApplicableFiltersQuery",
    alias: "getApplicableFiltersQuery",
    skip: ({ areFiltersApplicable, isProjectNode }) =>
      !areFiltersApplicable || isProjectNode,
    options: ({ module }) => {
      return { fetchPolicy: "cache-and-network", variables: { module } };
    },
    props: ({
      getApplicableFiltersQuery: { networkStatus },
      ownProps: { module, isData, isLoading, activeTab, curriculumType },
    }) => {
      const data = getApplicableFiltersFromCache({ module });

      const moduleFacets = getUpdatedModuleFacets({
        data,
        activeTab,
        curriculumType,
      });

      return {
        data,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        isData: !_.isEmpty(data) && isData,

        applicableFilters: getApplicableFiltersInfoMemoized({
          moduleFacets,
          activeTab,
        }),
      };
    },
  }),
  withLoader
)(DynamicFiltersWrapper);
